import { useState, useEffect } from 'react';
import { Megaphone, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAnnouncement, announcementDismissKey } from '../../hooks/useAnnouncement';

// Deliberately quiet: the app's normal card surface, no fill colour, no accent
// band. It sits just under the featured carousel — near the top so testers
// actually see it, but not competing with the hero, which would make it read as
// a promo rather than a note.
const AnnouncementCard = () => {
  const { announcement } = useAnnouncement();
  const [dismissedId, setDismissedId] = useState(null);

  // Read dismissal once the announcement arrives, since the key depends on its
  // id. Private browsing and blocked site data both throw here.
  useEffect(() => {
    if (!announcement) return;
    try {
      if (localStorage.getItem(announcementDismissKey(announcement.id)) === '1') {
        setDismissedId(announcement.id);
      }
    } catch {
      /* treat as not dismissed */
    }
  }, [announcement]);

  if (!announcement || dismissedId === announcement.id) return null;

  const handleDismiss = () => {
    setDismissedId(announcement.id);
    try {
      localStorage.setItem(announcementDismissKey(announcement.id), '1');
    } catch {
      // Dismissed for this session only — better than not dismissing at all.
    }
  };

  const date = announcement.created_at
    ? new Date(announcement.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  return (
    <div className="px-4 md:px-0 pt-1 pb-2 md:pt-0 md:pb-4">
      {/* One row, so a typical short announcement reads as a single line
          instead of a three-line card. The body is NOT clamped: there is no
          announcement detail view to expand into, so clipping it would strip
          text with nowhere to go — the same trap the event calendar had. A
          long body simply wraps. */}
      <div
        className="rounded-xl px-3.5 py-2.5 flex items-center gap-x-3 gap-y-1 flex-wrap"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
      >
        <Megaphone
          size={15}
          className="flex-shrink-0"
          style={{ color: 'var(--brand-primary)' }}
          strokeWidth={2}
        />

        <p className="flex-1 min-w-[12rem] text-sm leading-snug" style={{ color: 'var(--text-secondary)' }}>
          <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
            {announcement.label}:
          </span>{' '}
          <span className="whitespace-pre-wrap">{announcement.body}</span>
          {date && (
            <span className="hidden md:inline text-xs" style={{ color: 'var(--text-tertiary)' }}>
              {' '}· {date}
            </span>
          )}
        </p>

        {announcement.cta_text && announcement.cta_to && (
          <Link
            to={announcement.cta_to}
            className="inline-flex items-center gap-1 flex-shrink-0 text-xs font-semibold hover:underline focus-visible:underline"
            style={{ color: 'var(--brand-primary)' }}
          >
            {announcement.cta_text}
            <ArrowRight size={13} />
          </Link>
        )}

        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded-lg transition-opacity opacity-50 hover:opacity-100"
          style={{ color: 'var(--text-tertiary)' }}
          aria-label="Dismiss announcement"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementCard;
